/**
 * DmnFriendlyPreview.js
 *
 * Renderiza el outline DMN parseado (`DmnOutline` de la API) como una
 * narrativa paso a paso para usuarios funcionales: oculta `hitPolicy`,
 * `typeRef` técnicos, `## Dependencias`, encabezados de tabla, FEEL crudo.
 *
 * El outline original (markdown técnico) sigue siendo la fuente de verdad
 * para el parser/builder; esta vista es opt-in.
 *
 * El render es **literal**, no resumen: cada fila de la tabla se traduce
 * 1:1 a una oración "Si X y Y entonces Z = W". No condensa, no omite reglas.
 */

function _esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const TYPE_LABELS_ES = {
  string: 'texto',
  integer: 'entero',
  long: 'entero grande',
  double: 'número',
  boolean: 'sí o no',
  date: 'fecha',
  dateTime: 'fecha y hora',
  time: 'hora',
  any: 'cualquiera',
};
const TYPE_LABELS_EN = {
  string: 'text',
  integer: 'integer',
  long: 'long integer',
  double: 'number',
  boolean: 'yes or no',
  date: 'date',
  dateTime: 'date & time',
  time: 'time',
  any: 'any',
};

const I18N = {
  es: {
    empty: '— sin contenido —',
    inputsHeading: 'Lo que necesitamos saber',
    decisionsHeading: 'Decisiones',
    decisionLabel: 'Decisión',
    returns: 'Devuelve',
    rulesIntro: 'Reglas, en orden:',
    ifLead: 'Si',
    and: 'y',
    or: 'o',
    anyValue: 'sin importar',
    fallback: 'Para todo lo demás',
    arrow: '→',
    uses: 'Esta decisión usa:',
    formulaIntro: 'Fórmula:',
    contextIntro: 'Cálculos auxiliares:',
    listIntro: 'Lista de valores:',
    invocationIntro: 'Usa el cálculo reutilizable',
    relationIntro: 'Relación:',
    mlIntro: 'Predicción del modelo',
    scenariosHeading: 'Cómo lo probaremos',
    given: 'Dado',
    expected: 'Se espera',
    yes: 'sí',
    no: 'no',
    bkmsHeading: 'Cálculos reutilizables',
    knowledgeHeading: 'Fuentes de conocimiento',
    parameters: 'Parámetros',
    type: (t) => TYPE_LABELS_ES[t] || t,
  },
  en: {
    empty: '— empty —',
    inputsHeading: 'What we need to know',
    decisionsHeading: 'Decisions',
    decisionLabel: 'Decision',
    returns: 'Returns',
    rulesIntro: 'Rules, in order:',
    ifLead: 'If',
    and: 'and',
    or: 'or',
    anyValue: 'regardless of',
    fallback: 'For everything else',
    arrow: '→',
    uses: 'This decision uses:',
    formulaIntro: 'Formula:',
    contextIntro: 'Helper calculations:',
    listIntro: 'List of values:',
    invocationIntro: 'Uses the reusable calculation',
    relationIntro: 'Relation:',
    mlIntro: 'Model prediction',
    scenariosHeading: 'How we will test it',
    given: 'Given',
    expected: 'Expected',
    yes: 'yes',
    no: 'no',
    bkmsHeading: 'Reusable calculations',
    knowledgeHeading: 'Knowledge sources',
    parameters: 'Parameters',
    type: (t) => TYPE_LABELS_EN[t] || t,
  },
};

// ── Pretty-print scalars (numbers, strings, "true/false", "-") ─────────

function _isAnyMarker(s) {
  return s == null || String(s).trim() === '' || String(s).trim() === '-';
}

function _prettyValue(raw, lang) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s === '') return '';
  // Strings with quotes — strip the quotes and italicise the value for narrative
  const m = s.match(/^"(.*)"$/);
  if (m) return `<em>${_esc(m[1])}</em>`;
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    // Numeric — leave as-is (do not localise — the rule itself was authored that way)
    return _esc(s);
  }
  if (s === 'true') return I18N[lang].yes;
  if (s === 'false') return I18N[lang].no;
  // FEEL/comparison expression — show verbatim, lightly emphasised
  return `<code class="dmn-fp__expr">${_esc(s)}</code>`;
}

// Format a single input cell as a clause: "Cliente es *Premium*", "Monto > 100",
// "Mora es sí", "(sin importar Cliente)" if it's a `-` cell.
function _formatInputClause(columnName, cell, lang) {
  const t = I18N[lang];
  if (_isAnyMarker(cell)) {
    return `<span class="dmn-fp__any">(${t.anyValue} ${_esc(columnName)})</span>`;
  }
  const s = String(cell).trim();
  // Comparison operators are kept verbatim — feel rather than translate.
  if (/^(<=|>=|<|>|=|!=|≠|≤|≥)\s*/.test(s)) {
    return `${_esc(columnName)} ${_prettyValue(s, lang)}`;
  }
  // Range syntax: "[a..b]", "(a..b)" — show as-is
  if (/^[\[\(].*\.\..*[\]\)]$/.test(s)) {
    return `${_esc(columnName)} ${_prettyValue(s, lang)}`;
  }
  // Comma-separated alternatives → "es A o B"
  if (s.includes(',')) {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      const rendered = parts.map((p) => _prettyValue(p, lang)).join(` ${t.or} `);
      return `${_esc(columnName)} es ${rendered}`;
    }
  }
  // "true"/"false" → "es sí" / "es no"
  if (s === 'true' || s === 'false') {
    return `${_esc(columnName)} es ${_prettyValue(s, lang)}`;
  }
  // String literal → "es *Premium*"
  if (/^".*"$/.test(s)) {
    return `${_esc(columnName)} es ${_prettyValue(s, lang)}`;
  }
  // Default: name<space>value
  return `${_esc(columnName)} ${_prettyValue(s, lang)}`;
}

function _formatOutputClause(columnName, cell, lang) {
  const t = I18N[lang];
  return `${_esc(columnName)} = ${_prettyValue(cell, lang) || '<em>?</em>'}`;
}

// ── Section renderers ──────────────────────────────────────────────────

function _renderInputs(inputs, lang) {
  const t = I18N[lang];
  if (!inputs || inputs.length === 0) return '';
  const items = inputs.map((inp) => {
    const typeLabel = t.type(inp.type_ref);
    const allowed = inp.allowed_values && inp.allowed_values.length
      ? `, puede ser: ${inp.allowed_values.map((v) => `<em>${_esc(String(v).replace(/^"|"$/g, ''))}</em>`).join(', ')}`
      : '';
    return `<li class="dmn-fp__input">
      <span class="dmn-fp__input-name">${_esc(inp.name)}</span>
      <span class="dmn-fp__input-type">— ${_esc(typeLabel)}${allowed}</span>
    </li>`;
  }).join('');
  return `
    <section class="dmn-fp__section dmn-fp__section--inputs">
      <h3 class="dmn-fp__h">${_esc(t.inputsHeading)}</h3>
      <ul class="dmn-fp__inputs-list">${items}</ul>
    </section>
  `;
}

function _renderDecisionTable(dec, lang) {
  const t = I18N[lang];
  const logic = dec.logic;
  const inputCols = logic.input_clauses || [];
  const outputCols = logic.output_clauses || [];
  const outputName = (outputCols[0]) || dec.logic?.output?.name || 'resultado';
  const rules = logic.rules || [];

  const rulesHtml = rules.map((rule, idx) => {
    const inputs = rule.input_entries || [];
    const outputs = rule.output_entries || [];
    const allInputsAny = inputs.every(_isAnyMarker);

    let conditionHtml;
    if (allInputsAny) {
      conditionHtml = `<span class="dmn-fp__rule-fallback">${_esc(t.fallback)}</span>`;
    } else {
      const clauses = inputs
        .map((cell, i) => ({ cell, col: inputCols[i] || `(col ${i + 1})` }))
        .filter((c) => !_isAnyMarker(c.cell))
        .map((c) => _formatInputClause(c.col, c.cell, lang));
      conditionHtml = `<span class="dmn-fp__rule-if">${_esc(t.ifLead)}</span> ${
        clauses.join(` <span class="dmn-fp__rule-and">${_esc(t.and)}</span> `)
      }`;
    }

    const outcomeHtml = outputs.map((cell, i) => {
      const col = outputCols[i] || outputName;
      return _formatOutputClause(col, cell, lang);
    }).join(', ');

    return `
      <li class="dmn-fp__rule${allInputsAny ? ' is-fallback' : ''}">
        <span class="dmn-fp__rule-num">${idx + 1}.</span>
        <span class="dmn-fp__rule-body">
          ${conditionHtml}
          <span class="dmn-fp__rule-arrow">${_esc(t.arrow)}</span>
          <span class="dmn-fp__rule-outcome">${outcomeHtml}</span>
          ${rule.annotation ? `<span class="dmn-fp__rule-note">${_esc(rule.annotation)}</span>` : ''}
        </span>
      </li>
    `;
  }).join('');

  return `
    <p class="dmn-fp__returns"><strong>${_esc(t.returns)}:</strong>
      ${_esc(outputName)} (${_esc(t.type(dec.logic?.output?.type_ref || 'string'))})</p>
    <p class="dmn-fp__rules-intro">${_esc(t.rulesIntro)}</p>
    <ol class="dmn-fp__rules">${rulesHtml || '<li class="dmn-fp__rule is-empty">—</li>'}</ol>
  `;
}

function _renderLiteral(dec, lang) {
  const t = I18N[lang];
  const expr = dec.logic?.expression || '';
  return `
    <p class="dmn-fp__returns"><strong>${_esc(t.returns)}:</strong>
      ${_esc(dec.logic?.output?.name || 'resultado')} (${_esc(t.type(dec.logic?.output?.type_ref || 'string'))})</p>
    <p class="dmn-fp__formula-intro">${_esc(t.formulaIntro)}</p>
    <pre class="dmn-fp__formula"><code>${_esc(expr) || '—'}</code></pre>
  `;
}

function _renderContext(dec, lang) {
  const t = I18N[lang];
  const entries = dec.logic?.entries || [];
  const items = entries.map((e) => `
    <li><strong>${_esc(e.name)}</strong>:
      <code class="dmn-fp__expr">${_esc(e.value || '—')}</code></li>
  `).join('');
  return `
    <p class="dmn-fp__returns"><strong>${_esc(t.returns)}:</strong>
      ${_esc(dec.logic?.output?.name || 'resultado')} (${_esc(t.type(dec.logic?.output?.type_ref || 'string'))})</p>
    <p class="dmn-fp__formula-intro">${_esc(t.contextIntro)}</p>
    <ul class="dmn-fp__context">${items || '<li>—</li>'}</ul>
  `;
}

function _renderList(dec, lang) {
  const t = I18N[lang];
  const items = (dec.logic?.items || []).map((it) => `<li><code class="dmn-fp__expr">${_esc(it)}</code></li>`).join('');
  return `<p class="dmn-fp__formula-intro">${_esc(t.listIntro)}</p>
    <ul class="dmn-fp__list">${items || '<li>—</li>'}</ul>`;
}

function _renderInvocation(dec, lang) {
  const t = I18N[lang];
  const fn = dec.logic?.called_function || '?';
  const items = (dec.logic?.bindings || []).map((b) =>
    `<li><strong>${_esc(b.name)}</strong>: <code class="dmn-fp__expr">${_esc(b.value || '—')}</code></li>`,
  ).join('');
  return `<p class="dmn-fp__formula-intro">${_esc(t.invocationIntro)} <em>${_esc(fn)}</em></p>
    <ul class="dmn-fp__context">${items}</ul>`;
}

function _renderRelation(dec, lang) {
  const t = I18N[lang];
  const cols = dec.logic?.columns || [];
  const rows = dec.logic?.rows || [];
  return `<p class="dmn-fp__formula-intro">${_esc(t.relationIntro)}</p>
    <table class="dmn-fp__relation">
      <thead><tr>${cols.map((c) => `<th>${_esc(c)}</th>`).join('')}</tr></thead>
      <tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${_esc(c)}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>`;
}

function _renderMl(dec, lang) {
  const t = I18N[lang];
  const m = dec.logic || {};
  const rows = [];
  if (m.model_ref) rows.push(`<li><strong>Modelo</strong>: <code>${_esc(m.model_ref)}</code></li>`);
  if (m.model_version) rows.push(`<li><strong>Versión</strong>: <code>${_esc(m.model_version)}</code></li>`);
  if (m.confidence_threshold != null) rows.push(`<li><strong>Confianza mínima</strong>: ${_esc(String(m.confidence_threshold))}</li>`);
  if (m.fallback?.value != null) rows.push(`<li><strong>Plan B</strong>: <code>${_esc(String(m.fallback.value))}</code></li>`);
  return `<p class="dmn-fp__formula-intro">${_esc(t.mlIntro)}</p>
    <ul class="dmn-fp__context">${rows.join('')}</ul>`;
}

function _renderDecisionLogic(dec, lang) {
  const type = dec.logic?.type;
  switch (type) {
    case 'decisionTable':     return _renderDecisionTable(dec, lang);
    case 'literalExpression': return _renderLiteral(dec, lang);
    case 'context':           return _renderContext(dec, lang);
    case 'list':              return _renderList(dec, lang);
    case 'invocation':        return _renderInvocation(dec, lang);
    case 'relation':          return _renderRelation(dec, lang);
    case 'machineLearning':   return _renderMl(dec, lang);
    default: return `<p class="dmn-fp__formula-intro">—</p>`;
  }
}

function _depsForDecision(decisionName, dependencies) {
  const found = (dependencies || []).find((d) => d.decision === decisionName);
  return (found && found.requires) || [];
}

function _renderDecisions(decisions, dependencies, lang) {
  const t = I18N[lang];
  if (!decisions || decisions.length === 0) return '';
  const blocks = decisions.map((dec, idx) => {
    const reqs = _depsForDecision(dec.name, dependencies);
    const usesRow = reqs.length > 0
      ? `<p class="dmn-fp__uses">▸ ${_esc(t.uses)} ${reqs.map((r) => `<em>${_esc(r)}</em>`).join(', ')}</p>`
      : '';
    return `
      <article class="dmn-fp__decision">
        <header class="dmn-fp__decision-head">
          <span class="dmn-fp__step">${idx + 1}</span>
          <h4 class="dmn-fp__decision-title">${_esc(t.decisionLabel)}: ${_esc(dec.name)}</h4>
        </header>
        ${dec.description ? `<p class="dmn-fp__decision-desc">${_esc(dec.description)}</p>` : ''}
        ${_renderDecisionLogic(dec, lang)}
        ${usesRow}
      </article>
    `;
  }).join('');
  return `<section class="dmn-fp__section dmn-fp__section--decisions">
    <h3 class="dmn-fp__h">${_esc(t.decisionsHeading)}</h3>
    ${blocks}
  </section>`;
}

function _renderScenarios(scenarios, lang) {
  const t = I18N[lang];
  if (!scenarios || scenarios.length === 0) return '';
  const items = scenarios.map((sc) => {
    const inputs = Object.entries(sc.inputs || {})
      .map(([k, v]) => `<em>${_esc(k)}</em> = ${_prettyValue(v, lang)}`).join(' · ');
    const expected = Object.entries(sc.expected || {})
      .map(([k, v]) => `<em>${_esc(k)}</em> = ${_prettyValue(v, lang)}`).join(', ');
    return `<li class="dmn-fp__scenario">
      <p class="dmn-fp__scenario-title"><strong>${_esc(sc.name)}</strong></p>
      <p class="dmn-fp__scenario-given">${_esc(t.given)}: ${inputs || '—'}</p>
      <p class="dmn-fp__scenario-expected">${_esc(t.expected)}: ${expected || '—'}</p>
    </li>`;
  }).join('');
  return `<section class="dmn-fp__section dmn-fp__section--scenarios">
    <h3 class="dmn-fp__h">${_esc(t.scenariosHeading)}</h3>
    <ul class="dmn-fp__scenarios">${items}</ul>
  </section>`;
}

function _renderBkms(bkms, lang) {
  const t = I18N[lang];
  if (!bkms || bkms.length === 0) return '';
  const items = bkms.map((bkm) => {
    const params = (bkm.parameters || []).map((p) =>
      `<em>${_esc(p.name)}</em> (${_esc(t.type(p.type_ref))})`).join(', ');
    return `<article class="dmn-fp__bkm">
      <h4 class="dmn-fp__decision-title">${_esc(bkm.name)}</h4>
      <p class="dmn-fp__params"><strong>${_esc(t.parameters)}:</strong> ${params || '—'}</p>
    </article>`;
  }).join('');
  return `<section class="dmn-fp__section dmn-fp__section--bkms">
    <h3 class="dmn-fp__h">${_esc(t.bkmsHeading)}</h3>
    ${items}
  </section>`;
}

/**
 * Render the parsed DMN outline as friendly HTML for a non-technical user.
 *
 * @param {object|null} outline  The parsed DmnOutline (from /dmn-outline or /dmn-outline-lint).
 * @param {string} lang          'es' | 'en'
 * @returns {string}             HTML string, safe to set as innerHTML (input-escaped).
 */
export function renderFriendlyDmnOutline(outline, lang = 'es') {
  if (!outline) {
    return `<div class="dmn-fp__empty">${_esc(I18N[lang].empty)}</div>`;
  }
  const title = outline.title
    ? `<h2 class="dmn-fp__title">${_esc(outline.title)}</h2>`
    : '';
  return `
    <div class="dmn-fp">
      ${title}
      ${_renderInputs(outline.inputs, lang)}
      ${_renderDecisions(outline.decisions, outline.dependencies, lang)}
      ${_renderBkms(outline.bkms, lang)}
      ${_renderScenarios(outline.scenarios, lang)}
    </div>
  `;
}

export default { renderFriendlyDmnOutline };
